import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { useFrame } from '@/hooks/use-frame'

const LETTERS = 'BLUNTSHEEP'.split('')
const REPEAT = 10

const RibbonColumn: FC = (): ReactNode => (
    <div className='flex flex-col items-center gap-[1.15em]'>
        {Array.from({ length: REPEAT }, (_, block) => (
            <div key={block} className='flex flex-col items-center gap-[0.55em]'>
                {LETTERS.map((letter, index) => (
                    <span key={`${block}-${index}`} className='block leading-none'>{letter}</span>
                ))}
                <span className='block h-6 w-px bg-current opacity-50' />
            </div>
        ))}
    </div>
)

// Vertical B L U N T S H E E P ribbons on both edges, drifting at different speeds
// and leaning with scroll velocity. Difference blending keeps them readable on paper and night.
const Ribbons: FC = (): ReactNode => {
    const leftRef = useRef<HTMLDivElement>(null)
    const rightRef = useRef<HTMLDivElement>(null)
    const offsets = useRef({ left: 0, right: 0 })
    const span = useRef(0)

    useFrame(state => {
        const left = leftRef.current
        const right = rightRef.current
        if (!left || !right) return

        const lean = Math.max(-1, Math.min(1, state.velocity / 4000))
        if (!span.current) span.current = left.scrollHeight / REPEAT
        const block = span.current
        if (!block) return

        offsets.current.left = (state.scrollY * 0.35 + state.time * 14) % block
        offsets.current.right = (state.scrollY * 0.6 + state.time * 9) % block

        left.style.transform = `translate3d(0, ${-offsets.current.left}px, 0) skewY(${lean * 4}deg)`
        right.style.transform = `translate3d(0, ${offsets.current.right - block}px, 0) skewY(${lean * -4}deg)`
    })

    return (
        <div aria-hidden='true' className='pointer-events-none fixed inset-y-0 left-0 right-0 z-[70] hidden mix-blend-difference xs:block'>
            <div className='absolute inset-y-0 left-2 overflow-hidden tab:left-3'>
                <div ref={leftRef} className='mono-label text-[11px] tracking-normal text-white will-change-transform'>
                    <RibbonColumn />
                </div>
            </div>
            <div className='absolute inset-y-0 right-2 overflow-hidden tab:right-3'>
                <div ref={rightRef} className='mono-label text-[11px] tracking-normal text-white will-change-transform'>
                    <RibbonColumn />
                </div>
            </div>
        </div>
    )
}

export default Ribbons
